import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, UserRoundCheck } from 'lucide-react';
import { adminQueries, getErrorMessage, hasProblemType, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { ASSIGNABLE_PROPERTY_ROLES, formatDate, labelOf, PROPERTY_ROLE_LABELS, propertyRoleAssignSchema } from '@blimo/shared';
import {
  applyProblemToForm,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Form,
  FormField,
  FormItem,
  FormMessage,
  FormRootError,
  SelectField,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Skeleton,
  toast,
  ToggleGroup,
} from '@blimo/ui';
import { PartyLink, SourceBadge } from '../../components/data.jsx';
import { PartyPicker } from '../meters/party-picker.jsx';

const VIEWS = [
  { value: 'current', label: 'Aktuell' },
  { value: 'history', label: 'Inkl. beendeter' },
];

const ROLE_OPTIONS = ASSIGNABLE_PROPERTY_ROLES.map((role) => ({ value: role, label: PROPERTY_ROLE_LABELS[role] }));

/** "01.01.2020 – heute" @param {string|null|undefined} from @param {string|null|undefined} to */
const period = (from, to) => `${from ? formatDate(from) : 'unbekannt'} – ${to ? formatDate(to) : 'heute'}`;

/**
 * Objektrollen einer Liegenschaft (F-112): Kassenprüfer, Ableser … mit Historie. Zuordnen/Beenden nur mit
 * `properties.update` (UX – die API prüft erneut). Beenden setzt `valid_to` (gestern), die Historie bleibt.
 * @param {{ property: { id: number, name?: string|null }, canEdit: boolean }} props
 */
export function PropertyRolesCard({ property, canEdit }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [view, setView] = useState('current');
  const [assignOpen, setAssignOpen] = useState(false);
  const [ending, setEnding] = useState(/** @type {any} */ (null));
  const { data, error, isPending, refetch } = useQuery(adminQueries.propertyRoles(api, property.id, { history: view === 'history' }));
  const roles = data ?? [];

  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.admin.detail('properties', property.id) });

  async function endRole() {
    try {
      await api.admin.properties.endRole(property.id, ending.id);
      await refresh();
      toast.success('Rolle beendet');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Die Rolle konnte nicht beendet werden.'));
    }
  }

  return (
    <Card className="lg:col-span-2">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-1.5">
          <CardTitle as="h2">Objektrollen</CardTitle>
          <CardDescription>Kassenprüfer, Ableser und weitere Rollen an dieser Liegenschaft.</CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ToggleGroup label="Objektrollen anzeigen" value={view} onValueChange={(v) => v && setView(v)} options={VIEWS} />
          {canEdit && (
            <Button size="sm" onClick={() => setAssignOpen(true)}>
              <Plus aria-hidden="true" /> Rolle zuordnen
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <ErrorState compact error={error} onRetry={() => refetch()} />
        ) : isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : roles.length === 0 ? (
          <EmptyState
            compact
            icon={UserRoundCheck}
            title={view === 'current' ? 'Keine aktuellen Objektrollen' : 'Keine Objektrollen'}
            description="Kassenprüfer und Ableser werden hier zugeordnet."
          />
        ) : (
          <ul className="flex flex-col divide-y" aria-label="Objektrollen">
            {roles.map((role) => (
              <li key={role.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <Badge variant="secondary">{labelOf(PROPERTY_ROLE_LABELS, role.role)}</Badge>
                <PartyLink party={role.party ? { id: role.party.id, display_name: role.party.name } : null} />
                <span className="numeric text-xs text-muted-foreground">{period(role.valid_from, role.valid_to)}</span>
                {role.valid_to && <Badge variant="neutral">Beendet</Badge>}
                <SourceBadge source={role.source} />
                {canEdit && !role.valid_to && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto"
                    onClick={() => setEnding(role)}
                    aria-label={`${labelOf(PROPERTY_ROLE_LABELS, role.role)} ${role.party?.name ?? ''} beenden`.replace(/\s+/g, ' ')}
                  >
                    Beenden
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {canEdit && (
        <>
          <AssignRoleSheet property={property} open={assignOpen} onOpenChange={setAssignOpen} onAssigned={refresh} />
          <ConfirmDialog
            open={Boolean(ending)}
            onOpenChange={(open) => !open && setEnding(null)}
            title="Rolle beenden?"
            description={
              ending
                ? `${labelOf(PROPERTY_ROLE_LABELS, ending.role)}: ${ending.party?.name ?? 'Person'} – die Rolle endet mit dem gestrigen Tag und bleibt in der Historie sichtbar.`
                : undefined
            }
            confirmLabel="Beenden"
            destructive
            onConfirm={endRole}
          />
        </>
      )}
    </Card>
  );
}

/** @param {{ property: { id: number, name?: string|null }, open: boolean, onOpenChange: (open: boolean) => void, onAssigned: () => unknown }} props */
function AssignRoleSheet({ property, open, onOpenChange, onAssigned }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Rolle zuordnen</SheetTitle>
          <SheetDescription>{property.name}</SheetDescription>
        </SheetHeader>
        {open && <AssignRoleForm property={property} onDone={() => onOpenChange(false)} onAssigned={onAssigned} />}
      </SheetContent>
    </Sheet>
  );
}

function AssignRoleForm({ property, onDone, onAssigned }) {
  const api = useApi();
  const form = useForm({ resolver: zodResolver(propertyRoleAssignSchema), defaultValues: { party_id: '', role: 'auditor' } });
  const mutation = useMutation({ mutationFn: (values) => api.admin.properties.assignRole(property.id, values) });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync({ party_id: Number(values.party_id), role: values.role });
      await onAssigned();
      toast.success('Rolle zugeordnet');
      onDone();
    } catch (error) {
      if (hasProblemType(error, 'role-exists')) {
        form.setError('root.server', { type: 'server', message: 'Die Person hat diese Rolle an der Liegenschaft bereits.' });
        return;
      }
      applyProblemToForm(error, form.setError, { fields: ['party_id', 'role'] });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} noValidate className="flex flex-1 flex-col gap-4 px-4">
        <FormRootError />
        <SelectField name="role" label="Rolle" options={ROLE_OPTIONS} />
        <FormField
          name="party_id"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <PartyPicker label="Person" value={field.value} emptyLabel="Bitte Person suchen …" onChange={(id) => field.onChange(id)} />
              <FormMessage />
            </FormItem>
          )}
        />
        <SheetFooter className="px-0">
          <Button type="submit" loading={form.formState.isSubmitting}>
            Zuordnen
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>
            Abbrechen
          </Button>
        </SheetFooter>
      </form>
    </Form>
  );
}
